import React, { useState } from "react";
import { Modal, FormActions, Input, IconsLarge } from "../../shared";
import { companyApi, usersApi, authService } from "../../../services";
import type { Company } from "../../../types";

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (company: Company) => void;
}

export const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Nome da empresa é obrigatório");
      return;
    }
    const userId = authService.getUserData()?.id;
    if (userId == null) {
      setError("Usuário não identificado.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const createResponse = await companyApi.createCompany({ name: name.trim() });
      if (!createResponse.success || !createResponse.data) {
        setError(createResponse.error ?? "Falha ao criar empresa");
        setLoading(false);
        return;
      }
      const data = createResponse.data as { id: number; name: string; isActive?: boolean };
      const linkResponse = await usersApi.addCompany(userId, data.id);
      if (!linkResponse.success) {
        setError(linkResponse.error ?? "Falha ao vincular empresa ao usuário");
        setLoading(false);
        return;
      }
      onSuccess({
        id: data.id,
        name: data.name,
        isActive: data.isActive ?? true,
      });
      setName("");
      onClose();
    } catch {
      setError("Falha ao criar empresa");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nova empresa"
      icon={<IconsLarge.Company />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        <Input
        label="Nome da empresa"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          placeholder="Ex: Minha Empresa"
          disabled={loading}
          required
        />

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          submitType="button"
          submitText="Criar empresa"
        />
      </div>
    </Modal>
  );
};
